import { View, Text, TouchableOpacity, Image, Platform, Linking } from "react-native";

const NAVY = "#1B2A4A";

// Single source of truth for the partner logos — used on the marketing landing
// page AND the in-app Resources → Partners page.
export const PARTNERS = [
  { name: "Greater Twin Cities Afghan Hound Club", url: "https://www.gtcahc.com/", logo: "https://dashhound.app/club-logos/gtcahc.jpg" },
  { name: "North Country Kennel Club", url: "https://northcountrykennelclub.com/", logo: "https://dashhound.app/club-logos/north-country-kc.png" },
  { name: "Time to Fly Performance Dog Sports", url: "https://timetoflydogs.com/", logo: "https://dashhound.app/club-logos/time-to-fly.png" },
  { name: "Charleston Dog Training Club", url: "https://www.charlestondogtrainingclub.com/", logo: "https://dashhound.app/club-logos/charleston-dtc.png" },
  { name: "North Texas Pembroke Welsh Corgi Fanciers", url: "https://www.ntpwcf.org/", logo: "https://dashhound.app/club-logos/ntpwcf.jpg" },
  { name: "German Shepherd Dog Club of Minneapolis & St. Paul", url: "http://www.gsdcmsp.org", logo: "https://dashhound.app/club-logos/gsdcmsp.png" },
  { name: "C4 Pet Photography", url: "https://c4petphotos.com/", logo: "https://dashhound.app/club-logos/c4-pet-photography-v2.png" },
  { name: "Pawsitive Motion Canine Rehabilitation", url: "https://pawsitivemotionk9.com/", logo: "https://dashhound.app/club-logos/pawsitive-motion.png", hideName: true, wide: true },
  { name: "A & E Cyber Publishers", url: "https://www.aecyberpublishers.com/", logo: "https://dashhound.app/club-logos/aecp-bw.png", hideName: true, wide: true },
];

// `columns` switches to a fixed N-column grid with bigger logos (the dedicated
// Partners page). Without it, the logos flow/wrap compactly (landing section).
export function PartnersGrid({ isWide, columns }: { isWide: boolean; columns?: number }) {
  const grid = !!columns;
  const logoSize = grid ? (isWide ? 170 : 120) : (isWide ? 124 : 104);

  const renderItem = (p: any) => {
    const isC4 = p.name === "C4 Pet Photography";
    const wide = !!p.wide;
    // Logo box. In the card grid every logo fills the card width at a fixed
    // height, so wide + square logos read at a consistent size. On the landing
    // strip, wide logos get a larger rectangular box than the square club logos.
    const boxW: any = grid ? "100%" : wide ? Math.round(logoSize * 1.9) : logoSize;
    const boxH = grid ? (isWide ? 96 : 74) : wide ? Math.round(logoSize * 1.05) : logoSize;
    const itemWidth: any = grid ? `${100 / columns! - 3}%` : isC4 ? 200 : wide ? (isWide ? 260 : 210) : isWide ? 152 : 140;
    const cardStyle: any = grid
      ? {
          backgroundColor: "#FFFFFF",
          borderWidth: 1,
          borderColor: "#E9ECF1",
          borderRadius: 16,
          paddingVertical: isWide ? 26 : 18,
          paddingHorizontal: 16,
          marginBottom: isWide ? 22 : 16,
          ...(Platform.OS === "web"
            ? { boxShadow: "0 2px 10px rgba(27,42,74,0.06)" }
            : { shadowColor: "#1B2A4A", shadowOpacity: 0.06, shadowRadius: 8, shadowOffset: { width: 0, height: 2 } }),
        }
      : {};
    return (
      <TouchableOpacity
        key={p.name}
        onPress={() => Linking.openURL(p.url)}
        activeOpacity={0.7}
        style={{
          alignItems: "center",
          justifyContent: "center",
          width: itemWidth,
          minHeight: grid ? (isWide ? 172 : 134) : isWide ? 152 : 122,
          ...cardStyle,
        }}
      >
        {isC4 ? (
          // Text wordmark matching C4's logo (high-contrast serif)
          <View style={{ alignItems: "center", justifyContent: "center", height: boxH }}>
            <Text
              style={{
                fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
                fontSize: grid ? (isWide ? 24 : 18) : 19,
                fontWeight: "700",
                color: "#1A1A1A",
                textAlign: "center",
              }}
            >
              C4 Pet Photography
            </Text>
            <View style={{ height: 2, backgroundColor: "#1A1A1A", alignSelf: "stretch", marginTop: 8 }} />
          </View>
        ) : Platform.OS === "web" ? (
          <img src={p.logo} alt={p.name} style={{ width: boxW, height: boxH, objectFit: "contain" } as any} />
        ) : (
          <Image source={{ uri: p.logo }} style={{ width: boxW, height: boxH }} resizeMode="contain" />
        )}
        {/* Skip the caption for logos whose wordmark already spells out the name */}
        {!isC4 && !p.hideName ? (
          <Text style={{ fontSize: grid ? 13 : 12, fontWeight: "600", color: NAVY, textAlign: "center", marginTop: 12, lineHeight: 17 }} numberOfLines={2}>
            {p.name}
          </Text>
        ) : null}
      </TouchableOpacity>
    );
  };

  // Dedicated Partners page: fixed N-column card grid.
  if (grid) {
    return (
      <View style={{ flexDirection: "row", flexWrap: "wrap", justifyContent: "center", columnGap: isWide ? 22 : 14, maxWidth: 920, width: "100%", alignSelf: "center" }}>
        {PARTNERS.map(renderItem)}
      </View>
    );
  }

  // Landing section: 5 logos on the top row, 4 on the bottom row.
  const rowStyle: any = { flexDirection: "row", flexWrap: "wrap", justifyContent: "center", alignItems: "flex-start", gap: 18, width: "100%" };
  return (
    <View style={{ width: "100%", maxWidth: 1120, alignSelf: "center", rowGap: isWide ? 30 : 22 }}>
      <View style={rowStyle}>{PARTNERS.slice(0, 5).map(renderItem)}</View>
      <View style={rowStyle}>{PARTNERS.slice(5).map(renderItem)}</View>
    </View>
  );
}
